import React, { useState, useCallback, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import type { Position, PointItem, ElevationPoint, RouteData } from '../types';
import { CyclingMap } from '../components/cyclingMap/CyclingMap';
import { RouteNameOverlay } from '../components/cyclingMap/RouteNameOverlay';
import { StatsBar, type StatType } from '../components/statsBar/StatsBar';
import { RoutingProgressIndicator } from '../components/routing/RoutingProgressIndicator';
import { DetailPanel } from '../components/cyclingMap/DetailPanel';
import { SwipeHandle } from '../components/swipeHandle/SwipeHandle';
import { RealisticPowerCalculator } from '../components/powerDuration/RealisticPowerCalculator';
import SaveRouteModal from '../components/modals/SaveRouteModal';
import DownloadModal from '../components/modals/DownloadModal';
import WelcomeModal from '../components/modals/WelcomeModal';
import type { RouteProfile } from '../components/profileSelector/RouteProfileSelector';
import { DEFAULT_ROUTE_PROFILE, RouteProfileSelector } from '../components/profileSelector/RouteProfileSelector';
import { clearRouteCache } from '../utils/routeCalculation';
import { clearProxyCache } from '../utils/proxyCache';
import { saveFullRouteData, downloadRouteAsGPX, loadRouteWithFullData } from '../utils/routeFullDataUtils';
import { reverseGeocode, formatGeocodeResult } from '../utils/addressResolver';
import { geocodeResultToAddressFields, addressFieldsToGeocodeResult, formatAddressFromRoutePoint } from '../utils/geocodingConverter';
import { findNearestSegment } from '../utils/mapUtils';
import { RouteThumbnailGenerator } from '../utils/thumbnailGenerator';
import { ElevationThumbnailGenerator } from '../utils/elevationThumbnailGenerator';
import { blurActiveElement } from '../utils/modalUtils';
import { v4 as uuidv4 } from 'uuid';
import type { DropResult } from '@hello-pangea/dnd';
import { RouteStorageManager, StorageType } from '../services/storage';
import { useAuth } from '../contexts/AuthContext';
import { PlanningSessionManager } from '../utils/planningSessionStorage';
import { useCyclingToasts } from '../hooks/useCyclingToasts';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { AnalyticsService } from '../services/analytics/HybridAnalyticsService';
import SEO from '../components/SEO';

// Enhanced routing imports
import { useEnhancedRouting } from '../hooks/useEnhancedRouting';
import { routingConfig } from '../utils/simpleRoutingConfig';

import './CyclingMapPage.css';

const DETAIL_PANEL_STATE_KEY = 'cycling-buddy-detail-panel-state';

const isValidStatType = (value: unknown): value is StatType =>
  value === 'distance' ||
  value === 'duration' ||
  value === 'ascend' ||
  value === 'surface' ||
  value === null;

const CyclingMapPageComponent: React.FC = () => {
  // React Router hooks for URL-based route loading
  const { routeId: urlRouteId } = useParams<{ routeId: string }>();
  const navigate = useNavigate();
  
  const { user, getAccessToken, refreshSession } = useAuth();
  const toast = useCyclingToasts();
  const { setUserId } = useErrorHandler();
  const [points, setPoints] = useState<PointItem[]>([]);
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  
  // Power calculator state - single source of truth for all calculator-related data
  const [calculatedDuration, setCalculatedDuration] = useState<number | null>(null); // Duration from power calculator
  const [powerToWeight, setPowerToWeight] = useState<number>(() => {
    const saved = localStorage.getItem('cyclingbuddy_power_to_weight');
    return saved ? parseFloat(saved) : 2.0;
  });
  const [showOriginal, setShowOriginal] = useState<boolean>(() => {
    const saved = localStorage.getItem('cyclingbuddy_duration_auto_mode');
    return saved ? saved === 'true' : true;
  });
  const [averageSpeed, setAverageSpeed] = useState<number | null>(null);
  const [speedChartData, setSpeedChartData] = useState<any[]>([]);
  
  const [elevationData, setElevationData] = useState<ElevationPoint[]>([]);
  // Loading state machine instead of simple boolean
  type LoadingState = 'idle' | 'calculating' | 'rendering' | 'complete';
  // Initialize loading state to 'calculating' if we have a URL route ID or sessionStorage route to load
  const [loadingState, setLoadingState] = useState<LoadingState>(() => {
    if (urlRouteId) return 'calculating';
    if (sessionStorage.getItem('loadRoute')) return 'calculating';
    return 'idle';
  });
  
  // Track manual retry to prevent useEffect interference
  const isManualRetryRef = useRef(false);
  
  // Helper function for state transitions with logging
  const transitionLoadingState = useCallback((newState: LoadingState, _reason?: string) => {
    setLoadingState(newState);
  }, [loadingState]);
  const [hasRoutingError, setHasRoutingError] = useState(false);
  const [isSavingRoute, setIsSavingRoute] = useState(false);
  const [hoverPoint, setHoverPoint] = useState<number | null>(null);
  const [stickyHoverPoint, setStickyHoverPoint] = useState<number | null>(null);
  const [routeProfile, setRouteProfile] = useState<RouteProfile>(DEFAULT_ROUTE_PROFILE);
  const [activeStatType, setActiveStatType] = useState<StatType>(null);
  const [isDetailPanelExpanded, setIsDetailPanelExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false); // New state to prevent transform during transitions
  const [scrollToPoint, setScrollToPoint] = useState<string | undefined>(undefined);
  const [deletingPointId, setDeletingPointId] = useState<string | undefined>(undefined);
  const [highlightedSurface, setHighlightedSurface] = useState<string | null>(null);
  const [highlightedSegmentIndex, setHighlightedSegmentIndex] = useState<number>(0);
  const [highlightedClimbIndex, setHighlightedClimbIndex] = useState<number | null>(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  
  // Welcome modal for first-time visitors
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(() => {
    return !localStorage.getItem('cyclingbuddy_welcome_dismissed');
  });
  
  // Handler to close welcome modal and persist the dismissal
  const handleCloseWelcomeModal = useCallback(() => {
    localStorage.setItem('cyclingbuddy_welcome_dismissed', 'true');
    setWelcomeModalOpen(false);
  }, []);
  
  // Track last non-null active stat to decide when to clear climb highlight after collapse
  const lastActiveStatRef = useRef<StatType>(null);
  const hasRestoredDetailPanelStateRef = useRef(false);
  const [currentRouteName, setCurrentRouteName] = useState<string | null>(null);
  const [currentRouteId, setCurrentRouteId] = useState<string | null>(null);
  const [currentRouteOwnerId, setCurrentRouteOwnerId] = useState<string | null>(null);
  const [currentRouteIsPublic, setCurrentRouteIsPublic] = useState<boolean>(false);
  const [currentRouteSourceType, setCurrentRouteSourceType] = useState<'manual' | 'gpx_import' | 'gpx_optimized'>('manual');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Temporary route session ID for tracking calculations during planning phase
  const [routeSessionId, setRouteSessionId] = useState<string | null>(null);
  const [geocodingLoadingPoints, setGeocodingLoadingPoints] = useState<Set<string>>(new Set());

  // Ref to track immediate route modification state to avoid React batching issues
  const isRouteModifiedRef = useRef(false);
  
  // Ref to track if we're in the middle of clearing the map to prevent route reloading
  const isClearingMapRef = useRef(false);
  
  // Detect landscape orientation on mobile/tablet devices to hide route profile selector
  const isSmallScreen = useMediaQuery('(max-width: 900px)');
  const isShortScreen = useMediaQuery('(max-height: 600px)');
  const isOrientationLandscape = useMediaQuery('(orientation: landscape)');
  const isLandscape = (isSmallScreen || isShortScreen) && isOrientationLandscape;

  // Map state tracking for session storage
  const [mapCenter, setMapCenter] = useState<Position>({ lat: 50.118179, lng: 8.654897 });
  const [mapZoom, setMapZoom] = useState<number>(11);

  // Map view control for session restoration
  const [shouldUpdateMapView, setShouldUpdateMapView] = useState(false);
  const [targetMapCenter, setTargetMapCenter] = useState<Position | undefined>(undefined);
  const [targetMapZoom, setTargetMapZoom] = useState<number | undefined>(undefined);

  // Function to mark route as having unsaved changes
  const markRouteAsChanged = useCallback(() => {
    if (!hasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
    
    // Immediately mark route as modified in ref to avoid React batching issues
    isRouteModifiedRef.current = true;
    
    // Extract route metrics if available
    const routeMetrics = routeData ? {
      distance: routeData.distance,
      duration: routeData.duration,
      elevationGain: routeData.ascend
    } : null;
    
    // Save current planning state to session storage
    PlanningSessionManager.savePlanningSession(
      points,
      routeProfile,
      [mapCenter.lat, mapCenter.lng],
      mapZoom,
      currentRouteName,
      currentRouteId,
      true, // hasUnsavedChanges = true
      routeMetrics,
      routeData, // Pass current route data
      elevationData // Pass current elevation data
    );
  }, [hasUnsavedChanges, points, routeProfile, mapCenter, mapZoom, currentRouteName, currentRouteId, routeData, elevationData]);

  // Route session management for tracking planning phases
  const initializeRouteSession = useCallback((pointsCount?: number, forceEditingSession?: boolean) => {
    const actualPointsCount = pointsCount ?? points.length;
    // Create a session if we don't have one and we're in planning/editing mode
    // For editing sessions, we can force creation even if hasUnsavedChanges isn't updated yet
    const shouldCreateSession = !routeSessionId && actualPointsCount >= 2 && 
      (!currentRouteId || hasUnsavedChanges || forceEditingSession);
    
    if (shouldCreateSession) {
      const sessionId = `route-session-${uuidv4()}`;
      setRouteSessionId(sessionId);
      // Store session in sessionStorage to persist across page reloads
      sessionStorage.setItem('routeSessionId', sessionId);
      return sessionId;
    } else if (routeSessionId) {
      return routeSessionId; // Return existing session instead of creating new one
    } 
    return routeSessionId;
  }, [routeSessionId, points.length, currentRouteId, hasUnsavedChanges]);

  // Clear route session when starting fresh or loading saved route
  const clearRouteSession = useCallback(() => {
    if (routeSessionId) {
      setRouteSessionId(null);
      // Remove from sessionStorage as well
      sessionStorage.removeItem('routeSessionId');
    }
  }, [routeSessionId]);

  // Get current session info for debugging/analytics (when ready)
  const getRouteSessionInfo = useCallback(() => {
    const storedSessionId = sessionStorage.getItem('routeSessionId');
    const sessionType = currentRouteId ? 'editing' : 'planning';
    const sessionInfo = {
      sessionId: routeSessionId,
      storedSessionId: storedSessionId,
      sessionInSync: routeSessionId === storedSessionId,
      sessionType: sessionType,
      routeId: currentRouteId,
      isTemporary: !currentRouteId,
      pointCount: points.length,
      hasRoute: !!routeData,
      hasUnsavedChanges: hasUnsavedChanges
    };
    
    return sessionInfo;
  }, [routeSessionId, currentRouteId, points.length, routeData, hasUnsavedChanges]);

  // Note: Duration calculation is now handled by the always-rendered RealisticPowerCalculator
  // It will automatically recalculate when route changes and respect the saved Auto mode setting

  // Save session whenever points change (immediate save for work-in-progress)
  useEffect(() => {
    // Save session if:
    // 1. We have points AND
    // 2. Either we're working on a new route (no currentRouteId) OR
    // 3. We're working on a saved route that has been modified (hasUnsavedChanges is true)
    // Skip saving only when:
    // - We just loaded from URL without modifications
    // - We have no points
    const shouldSaveSession = points.length > 0 && 
      (!hasLoadedFromUrlRef.current || hasUnsavedChanges) &&
      (!currentRouteId || hasUnsavedChanges);

    if (shouldSaveSession) {
      const routeMetrics = routeData ? {
        distance: routeData.distance,
        duration: routeData.duration,
        elevationGain: routeData.ascend
      } : null;
      
      PlanningSessionManager.savePlanningSession(
        points,
        routeProfile,
        [mapCenter.lat, mapCenter.lng],
        mapZoom,
        currentRouteName,
        currentRouteId,
        hasUnsavedChanges,
        routeMetrics,
        routeData,
        elevationData
      );
    }
  }, [points, routeProfile, mapCenter, mapZoom, currentRouteName, currentRouteId, hasUnsavedChanges, routeData, elevationData]);

  // Restore detail panel visibility state when returning to the page
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (hasRestoredDetailPanelStateRef.current) {
      return;
    }

    const storedState = sessionStorage.getItem(DETAIL_PANEL_STATE_KEY);

    if (!storedState) {
      hasRestoredDetailPanelStateRef.current = true;
      return;
    }

    try {
      const parsed = JSON.parse(storedState) as {
        activeStatType?: StatType | null;
        isExpanded?: boolean;
      };

      const storedActiveStat = Object.prototype.hasOwnProperty.call(parsed, 'activeStatType')
        ? parsed.activeStatType
        : null;

      const storedExpanded = parsed.isExpanded ?? false;

      if (storedActiveStat && (!routeData || points.length === 0)) {
        // Wait until route data is available before restoring an open panel
        return;
      }

      if (isValidStatType(storedActiveStat)) {
        setActiveStatType(storedActiveStat);
        setIsDetailPanelExpanded(storedActiveStat ? Boolean(storedExpanded) : false);
      } else {
        setActiveStatType(null);
        setIsDetailPanelExpanded(false);
        sessionStorage.removeItem(DETAIL_PANEL_STATE_KEY);
      }

      hasRestoredDetailPanelStateRef.current = true;
    } catch (error) {
      console.warn('Failed to restore detail panel state from sessionStorage', error);
      sessionStorage.removeItem(DETAIL_PANEL_STATE_KEY);
      hasRestoredDetailPanelStateRef.current = true;
    }
  }, [routeData, points.length]);

  // Persist detail panel state for navigation away/back scenarios
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!hasRestoredDetailPanelStateRef.current) {
      // Avoid overwriting stored state before initial restoration
      return;
    }

    try {
      sessionStorage.setItem(
        DETAIL_PANEL_STATE_KEY,
        JSON.stringify({
          activeStatType,
          isExpanded: isDetailPanelExpanded
        })
      );
    } catch (error) {
      console.warn('Failed to persist detail panel state', error);
    }
  }, [activeStatType, isDetailPanelExpanded]);

  // Initialize error tracking with user ID
  useEffect(() => {
    setUserId(user?.id);
  }, [user, setUserId]);

  // Track page view for analytics
  useEffect(() => {
    AnalyticsService.trackPageView('cycling_map');
  }, []); // Run once on component mount

  // Enhanced routing callbacks - memoized to prevent infinite loops
  const handleRoutingComplete = useCallback((result: RouteData | null) => {
    // If calculation was cancelled, ignore the result
    if (routeCalculationRef.current.wasCancelled) {
      console.log('Ignoring route result - calculation was cancelled');
      routeCalculationRef.current.wasCancelled = false; // Reset flag
      return;
    }
    
    if (result) {
      setRouteData(result);
      setElevationData(result.elevationData);
      setHasRoutingError(false);

      // Save session with the fresh route metrics
      const routeMetrics = {
        distance: result.distance,
        duration: result.duration,
        elevationGain: result.ascend
      };
      
      PlanningSessionManager.savePlanningSession(
        points,
        routeProfile,
        [mapCenter.lat, mapCenter.lng],
        mapZoom,
        currentRouteName,
        currentRouteId,
        hasUnsavedChanges,
        routeMetrics,
        result,
        result.elevationData
      );

      transitionLoadingState('rendering', 'route calculated successfully');
      
      // Set fallback timeout
      fallbackTimeoutRef.current = window.setTimeout(() => {
        transitionLoadingState('idle', 'fallback timeout');
        fallbackTimeoutRef.current = null;
      }, 2000);
    } else {
      setRouteData(null);
      setElevationData([]);
      setTimeout(() => {
        transitionLoadingState('idle', 'route calculation failed');
      }, 400);
    }
    // Reset legacy loading state
    routeCalculationRef.current.isCalculating = false;
  }, [transitionLoadingState]);

  const handleRoutingError = useCallback((error: Error) => {
    console.error('Enhanced route calculation failed:', error);
    setHasRoutingError(true);
    setRouteData(null);
    setElevationData([]);
    setActiveStatType(null); // Close detail panel when error occurs
    // Reset legacy loading state
    setTimeout(() => {
      transitionLoadingState('idle', 'enhanced routing error');
    }, 400);
    routeCalculationRef.current.isCalculating = false;
  }, [transitionLoadingState]);

  const handleRoutingCancel = useCallback(() => {
    setHasRoutingError(false);
    // Don't clear routeData/elevationData here - handleCancelWithUndo already restored the previous state
    setActiveStatType(null); // Close detail panel when cancelling calculation
    // Reset legacy loading state
    transitionLoadingState('idle', 'enhanced routing cancelled');
    routeCalculationRef.current.isCalculating = false;
  }, [transitionLoadingState]);

  // Enhanced routing hook with stable callbacks
  const {
    isCalculating: isEnhancedCalculating,
    progress: enhancedProgress,
    progressPercent: enhancedProgressPercent,
    timeElapsed: enhancedTimeElapsed,
    targetDuration: enhancedTargetDuration,
    error: enhancedError,
    canCancel: canCancelEnhanced,
    calculateRoute: calculateEnhancedRoute,
    cancelCalculation: cancelEnhancedCalculation,
    clearError: clearEnhancedError,
    retryLastCalculation
  } = useEnhancedRouting({
    onComplete: handleRoutingComplete,
    onError: handleRoutingError,
    onCancel: handleRoutingCancel,
    defaultTimeout: routingConfig.getPreferences().maxTimeout
  });

  // Combine legacy and enhanced loading states for map spinner
  const isLoadingEnhanced = (loadingState !== 'idle' && loadingState !== 'complete') || isEnhancedCalculating;

  // Save session when route is successfully saved (has name and ID but no unsaved changes)
  useEffect(() => {
    if (currentRouteName && currentRouteId && !hasUnsavedChanges && points.length > 0) {
      // Extract route metrics if available
      const routeMetrics = routeData ? {
        distance: routeData.distance,
        duration: routeData.duration,
        elevationGain: routeData.ascend
      } : null;
      
      PlanningSessionManager.savePlanningSession(
        points,
        routeProfile,
        [mapCenter.lat, mapCenter.lng],
        mapZoom,
        currentRouteName,
        currentRouteId,
        false, // No unsaved changes after successful save
        routeMetrics,
        routeData, // Pass current route data
        elevationData // Pass current elevation data
      );
    }
  }, [currentRouteName, currentRouteId, hasUnsavedChanges, points, routeProfile, mapCenter, mapZoom, routeData, elevationData]);

  // Handle map state changes (center and zoom)
  const handleMapStateChange = useCallback((center: Position, zoom: number) => {
    setMapCenter(center);
    setMapZoom(zoom);
    
    // Save to session storage whenever map view changes (but only if we have route points)
    if (points.length > 0) {
      // Extract route metrics if available
      const routeMetrics = routeData ? {
        distance: routeData.distance,
        duration: routeData.duration,
        elevationGain: routeData.ascend
      } : null;
      
      PlanningSessionManager.savePlanningSession(
        points,
        routeProfile,
        [center.lat, center.lng],
        zoom,
        currentRouteName,
        currentRouteId,
        hasUnsavedChanges,
        routeMetrics,
        routeData, // Pass current route data
        elevationData // Pass current elevation data
      );
    }
  }, [points, routeProfile, currentRouteName, currentRouteId, hasUnsavedChanges, routeData, elevationData]);

  // Wrapper function to track route profile changes
  const handleRouteProfileChange = useCallback((newProfile: RouteProfile) => {
    setRouteProfile(newProfile);
    markRouteAsChanged(); // Route profile change modifies the route
  }, [markRouteAsChanged, routeProfile]);

  // History system for undo/redo functionality
  const [routeHistory, setRouteHistory] = useState<{
    points: PointItem[];
    routeData: RouteData | null;
    elevationData: ElevationPoint[];
  }[]>([]);
  
  const [redoHistory, setRedoHistory] = useState<{
    points: PointItem[];
    routeData: RouteData | null;
    elevationData: ElevationPoint[];
  }[]>([]);

  // Helper function to reset undo/redo history
  const resetHistory = useCallback(() => {
    setRouteHistory([]);
    setRedoHistory([]);
  }, []);

  // Map fit function reference
  const fitMapToRouteRef = useRef<((points: Array<{ lat: number; lon: number }>) => void) | null>(null);
  
  // Track if the last point was added via location search (to trigger auto-fit)
  const lastPointViaSearchRef = useRef<boolean>(false);
  
  // Track when a route has been loaded and needs to be fitted to the map
  const needsAutoFitRef = useRef<boolean>(false);

  // Helper function to get the correct user ID
  const getUserId = useCallback(() => {
    // If user is authenticated, use their ID; otherwise use the local storage ID
    return user?.id || RouteStorageManager.getDefaultUserId();
  }, [user]);

  // Initialize storage manager
  const storageManagerRef = useRef<RouteStorageManager | null>(null);
  
  // Initialize storage manager on first render
  useEffect(() => {
    // Use Supabase storage provider (standard Supabase approach)
    // Initialize for both authenticated and unauthenticated users
    storageManagerRef.current = new RouteStorageManager({
      type: StorageType.SUPABASE,
      endpoint: import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000' // Supabase Kong gateway
    }, user ? {
      getAccessToken,
      refreshSession
    } : undefined); // Allow undefined auth context for public access
  }, [user, getAccessToken, refreshSession]);

  // Ref to track whether we've loaded a route from URL to prevent session loading
  const hasLoadedFromUrlRef = useRef(false);
  
  // Ref to track session restoration to prevent route clearing
  const isRestoringSessionRef = useRef(false);
  
  // Ref to track error state restoration to prevent recalculation
  const isRestoringFromErrorRef = useRef(false);
  
  // Load planning session on component mount (but only if no saved route is being loaded)
  useEffect(() => {
    const loadSessionData = () => {
      try {
        // Check if there's a route to be loaded from URL path or sessionStorage first
        const storedRouteData = sessionStorage.getItem('loadRoute');
        
        // If there's a route to be loaded, skip session restoration - saved route takes priority
        if (urlRouteId || storedRouteData || hasLoadedFromUrlRef.current) {
          return;
        }
        
        const sessionState = PlanningSessionManager.loadPlanningSession();
        
        if (sessionState) {
          // Validate session state has required properties
          if (!sessionState.points || !Array.isArray(sessionState.points)) {
            console.warn('⚠️ Invalid session state: points is missing or not an array');
            // Clear corrupted session data
            PlanningSessionManager.clearPlanningSession();
            return;
          }
          
          // Mark that we're restoring session to prevent route clearing
          isRestoringSessionRef.current = true;
          
          // Restore route state - set route data BEFORE points to prevent useEffect issues
          
          // First, restore calculated route data and elevation data if available
          if (sessionState.routeData) {
            setRouteData(sessionState.routeData);
            
            // If we have complete route data, set calculation key to prevent recalculation
            const positions = sessionState.points
              .map(p => p.position)
              .filter((pos): pos is Position => pos !== null);
            if (positions.length >= 2) {
              const calculationKey = `${positions.map(p => `${p.lat},${p.lng}`).join('|')}-${sessionState.routeProfile}`;
              routeCalculationRef.current.lastCalculationKey = calculationKey;
            }
          }
          if (sessionState.elevationData && sessionState.elevationData.length > 0) {
            setElevationData(sessionState.elevationData);
          }
          
          // Then restore points (this may trigger useEffect, but route data is already set)
          setPoints(sessionState.points);
          setRouteProfile(sessionState.routeProfile);
          setCurrentRouteName(sessionState.currentRouteName);
          setCurrentRouteId(sessionState.currentRouteId);
          setHasUnsavedChanges(sessionState.hasUnsavedChanges);
          
          // Restore map state safely
          if (sessionState.mapCenter) {
            setMapCenter(sessionState.mapCenter);
            setTargetMapCenter(sessionState.mapCenter);
          }
          if (sessionState.mapZoom) {
            setMapZoom(sessionState.mapZoom);
            setTargetMapZoom(sessionState.mapZoom);
          }
          
          // Trigger map view update if we have valid coordinates
          if (sessionState.mapCenter) {
            setShouldUpdateMapView(true);
            
            // Reset the update flag after a delay to allow the map to update
            setTimeout(() => {
              setShouldUpdateMapView(false);
              setTargetMapCenter(undefined);
              setTargetMapZoom(undefined);
            }, 2000); // Give enough time for the map to update
          }
          
          // If we have route points, trigger route calculation to restore the full route
          if (sessionState.points.length >= 2) {
            // Note: The route calculation will be triggered by the points state change
            // We won't auto-fit since we want to keep the saved view
          }
          
          // Clear session restoration flag after a delay to allow state updates to complete
          setTimeout(() => {
            isRestoringSessionRef.current = false;
          }, 100);
        } else {
          // No planning session found to restore
        }
      } catch (error) {
        console.error('❌ Failed to load planning session:', error);
      }
    };

    // Load session data after component mounts
    loadSessionData();
  }, []); // Empty dependency array - run only on mount

  // Helper function to generate and upload both route and elevation thumbnails
  const generateAndUploadThumbnails = useCallback(async (routeId: string): Promise<{ routeThumbnail: string | null, elevationThumbnail: string | null }> => {
    const results = {
      routeThumbnail: null as string | null,
      elevationThumbnail: null as string | null
    };

    // Generate route thumbnail (existing logic)
    if (routeData?.coordinates && routeData.coordinates.length >= 2) {
      try {
        
        const thumbnailBlob = await RouteThumbnailGenerator.generateThumbnail(
          routeData.coordinates
        );
        
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxvY2FsaG9zdCIsInJvbGUiOiJhbm9uIiwiaWF0IjoxNzM2NzA1NjAwLCJleHAiOjE4NTIzNjI3MDB9.a2kcrdv2Ydk4yHxB4HTkXUdFna-nt4r-OgQW-Tq8a_k';
        const accessToken = await getAccessToken();
        const authToken = accessToken || anonKey;
        
        const timestamp = Date.now();
        const filename = `route-${routeId}-${timestamp}.png`;
        
        const formData = new FormData();
        formData.append('file', thumbnailBlob, filename);
        
        const uploadResponse = await fetch(`${supabaseUrl}/storage/v1/object/route-thumbnails/${filename}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'apikey': anonKey
          },
          body: formData
        });

        if (uploadResponse.ok) {
          results.routeThumbnail = filename;
        } else {
          console.error('Failed to upload route thumbnail:', uploadResponse.status);
        }
      } catch (error) {
        console.error('Error generating/uploading route thumbnail:', error);
      }
    }

    // Generate elevation thumbnail (new functionality)
    if (elevationData && elevationData.length > 0) {
      try {
        
        const elevationThumbnailBlob = await ElevationThumbnailGenerator.generateThemedThumbnail(
          elevationData,
          routeProfile,
          'medium'
        );
        
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxvY2FsaG9zdCIsInJvbGUiOiJhbm9uIiwiaWF0IjoxNzM2NzA1NjAwLCJleHAiOjE4NTIzNjI3MDB9.a2kcrdv2Ydk4yHxB4HTkXUdFna-nt4r-OgQW-Tq8a_k';
        const accessToken = await getAccessToken();
        const authToken = accessToken || anonKey;
        
        const timestamp = Date.now();
        const elevationFilename = `elevation-${routeId}-${timestamp}.png`;
        
        const elevationFormData = new FormData();
        elevationFormData.append('file', elevationThumbnailBlob, elevationFilename);
        
        const elevationUploadResponse = await fetch(`${supabaseUrl}/storage/v1/object/elevation-thumbnails/${elevationFilename}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'apikey': anonKey
          },
          body: elevationFormData
        });

        if (elevationUploadResponse.ok) {
          results.elevationThumbnail = elevationFilename;
        } else {
          console.error('Failed to upload elevation thumbnail:', elevationUploadResponse.status);
        }
      } catch (error) {
        console.error('Error generating/uploading elevation thumbnail:', error);
      }
    }

    return results;
  }, [routeData, elevationData, routeProfile, getAccessToken]);

  // Helper function to generate and upload route thumbnail (backward compatibility)
  const generateAndUploadThumbnail = useCallback(async (routeId: string): Promise<string | null> => {
    const results = await generateAndUploadThumbnails(routeId);
    return results.routeThumbnail;
  }, [generateAndUploadThumbnails]);

  // Ref to prevent concurrent route calculations
  const routeCalculationRef = useRef<{
    isCalculating: boolean;
    lastCalculationKey: string;
    wasCancelled: boolean;
  }>({
    isCalculating: false,
    lastCalculationKey: '',
    wasCancelled: false
  });

  // Ref to track fallback timeout for cancellation
  const fallbackTimeoutRef = useRef<number | null>(null);

  // Function to save current state to history (before making changes)
  const saveToHistory = useCallback(() => {
    setRouteHistory(prev => {
      const newHistory = [...prev, {
        points: [...points],
        routeData: routeData ? { ...routeData } : null,
        elevationData: [...elevationData]
      }];
      // Keep only last 10 states to prevent memory issues
      return newHistory.slice(-10);
    });
    // Clear redo history when a new action is performed
    setRedoHistory([]);
  }, [points, routeData, elevationData]);

  // Helper function to start reverse geocoding for a point
  const startGeocodingForPoint = useCallback(async (pointId: string, position: Position, forceResolve: boolean = false) => {
    try {
      // Skip geocoding for route points from uploaded GPX files (except start/end points)
      // This helps reduce Nominatim API calls for fair use compliance
      const point = points.find(p => p.id === pointId);
      if (!forceResolve && point) {
        const pointIndex = points.findIndex(p => p.id === pointId);
        const isStartOrEnd = pointIndex === 0 || pointIndex === points.length - 1;
        
        // Skip geocoding for middle points of uploaded routes
        if (!isStartOrEnd && points.length > 2) {
          // Check if this is an uploaded route based on source type
          if (currentRouteSourceType === 'gpx_import' || currentRouteSourceType === 'gpx_optimized') {
            return;
          }
        }
      }
      
      const geocodeResult = await reverseGeocode(position);
      const address = formatGeocodeResult(geocodeResult);
      
      // Update the point with both formatted address and structured data
      setPoints(prevPoints => 
        prevPoints.map(point => 
          point.id === pointId 
            ? { ...point, address, geocodeResult }
            : point
        )
      );
    } catch (error) {
      console.warn('Failed to resolve address for point:', pointId, error);
    }
  }, [points, currentRouteSourceType]);

  // Callback to handle fitMapToRoute function from MapControls
  const handleFitMapToRoute = useCallback((fitFunction: (points: Array<{ lat: number; lon: number }>) => void) => {
    fitMapToRouteRef.current = fitFunction;
  }, []);

  // Auto-fit map when route is loaded and fitMapToRoute function becomes available
  useEffect(() => {
    if (needsAutoFitRef.current && fitMapToRouteRef.current && points.length > 0) {
      // Use setTimeout to ensure the route polyline is fully rendered
      const timeoutId = setTimeout(() => {
        if (fitMapToRouteRef.current && points.length > 0) {
          const pointPositions = points
            .filter(point => point.position)
            .map(point => ({ lat: point.position!.lat, lon: point.position!.lng }));
          
          if (pointPositions.length > 0) {
            fitMapToRouteRef.current(pointPositions);
            needsAutoFitRef.current = false; // Reset flag after fitting
          }
        }
      }, 300); // Wait for route to be fully rendered
      
      return () => clearTimeout(timeoutId);
    }
  }, [points, fitMapToRouteRef.current]); // Re-run when points change or fitMapToRoute becomes available

  // Function to fit map to all route points
  const fitMapToAllPoints = useCallback(() => {
    if (fitMapToRouteRef.current && points.length > 0) {
      const pointPositions = points
        .filter(point => point.position)
        .map(point => ({ lat: point.position!.lat, lon: point.position!.lng }));
      
      if (pointPositions.length > 0) {
        fitMapToRouteRef.current(pointPositions);
      }
    }
  }, [points]);

  // Handle route rendered callback with debouncing to prevent multiple rapid calls
  const routeRenderedTimeoutRef = useRef<number | null>(null);
  const handleRouteRendered = useCallback(() => {

    // Cancel fallback timeout since route rendered successfully
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    
    // Only clear loading if we're in the rendering state
    if (loadingState === 'rendering') {
      
      // Clear any existing timeout
      if (routeRenderedTimeoutRef.current) {
        clearTimeout(routeRenderedTimeoutRef.current);
      }
      
      // Set a short timeout to transition to complete state
      routeRenderedTimeoutRef.current = window.setTimeout(() => {
        transitionLoadingState('complete', 'route rendered successfully');
        // Immediately transition back to idle for next operation
        setTimeout(() => transitionLoadingState('idle', 'ready for next operation'), 50);
        routeRenderedTimeoutRef.current = null;
      }, 50);
    } 
  }, [loadingState]);

  // Function to clear the sticky hover point
  const clearStickyHover = useCallback(() => {
    setStickyHoverPoint(null);
    setHoverPoint(null);
  }, []);

  // Handle surface highlighting
  const handleSurfaceHighlight = useCallback((surface: string | null, segmentIndex: number = 0) => {
    setHighlightedSurface(surface);
    setHighlightedSegmentIndex(segmentIndex);
    
    // Handle different highlighting modes
    if (surface && routeData?.segments && fitMapToRouteRef.current) {
      if (segmentIndex === -1) {
        // Special case: highlight all segments of this surface type and fit complete route
        const allRoutePoints = routeData.coordinates.map(coord => ({ 
          lat: coord.lat, 
          lon: coord.lng 
        }));
        
        // Fit to the complete route
        fitMapToRouteRef.current(allRoutePoints);
      } else {
        // Original behavior: fit to specific segment
        const surfaceSegments = routeData.segments.filter(seg => seg.surface === surface);
        
        if (surfaceSegments[segmentIndex] && routeData.coordinates) {
          // Find the target segment in the original segments array
          const targetSegment = surfaceSegments[segmentIndex];
          let cumulativeDistance = 0;
          let segmentStartDistance = 0;
          let segmentEndDistance = 0;
          
          // Find the distance range for the target segment
          for (let i = 0; i < routeData.segments.length; i++) {
            const segment = routeData.segments[i];
            const segmentDistance = segment.Distance ? Number(segment.Distance) : 0;
            
            if (segment === targetSegment) {
              segmentStartDistance = cumulativeDistance;
              segmentEndDistance = cumulativeDistance + segmentDistance;
              break;
            }
            cumulativeDistance += segmentDistance;
          }
          
          // Calculate route coordinate distances to find the segment coordinates
          const routeDistances = [0];
          for (let i = 1; i < routeData.coordinates.length; i++) {
            const prevPoint = routeData.coordinates[i - 1];
            const currentPoint = routeData.coordinates[i];
            
            // Calculate distance between consecutive points using Haversine formula
            const R = 6371000; // Earth's radius in meters
            const φ1 = (prevPoint.lat * Math.PI) / 180;
            const φ2 = (currentPoint.lat * Math.PI) / 180;
            const Δφ = ((currentPoint.lat - prevPoint.lat) * Math.PI) / 180;
            const Δλ = ((currentPoint.lng - prevPoint.lng) * Math.PI) / 180;

            const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
                     Math.cos(φ1) * Math.cos(φ2) *
                     Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const distanceBetweenPoints = R * c;
            
            routeDistances.push(routeDistances[i - 1] + distanceBetweenPoints);
          }
          
          // Find coordinate indices for the segment start and end
          let startIndex = 0;
          let endIndex = routeData.coordinates.length - 1;
          
          for (let i = 0; i < routeDistances.length; i++) {
            if (routeDistances[i] >= segmentStartDistance && startIndex === 0) {
              startIndex = Math.max(0, i - 1); // Include one point before for context
            }
            if (routeDistances[i] >= segmentEndDistance) {
              endIndex = Math.min(routeData.coordinates.length - 1, i + 1); // Include one point after for context
              break;
            }
          }
          
          // Extract the coordinates for this segment
          const segmentCoordinates = routeData.coordinates.slice(startIndex, endIndex + 1);
          
          if (segmentCoordinates.length > 0) {
            // Convert to the format expected by fitMapToRoute
            const segmentPoints = segmentCoordinates.map(coord => ({ 
              lat: coord.lat, 
              lon: coord.lng 
            }));
            
            // Use the fitMapToRoute function to fit the segment with DetailPanel-aware padding
            fitMapToRouteRef.current(segmentPoints);
          }
        }
      }
    }
  }, [routeData]);

  // Handle climb highlighting - fit map to climb segment
  const handleClimbHighlight = useCallback((climbIndex: number | null) => {
    setHighlightedClimbIndex(climbIndex);
    
    if (climbIndex !== null && routeData?.climbs && routeData.climbs.length > 0 && fitMapToRouteRef.current) {
      const climbs = routeData.climbs;
      
      if (climbs[climbIndex] && routeData?.coordinates) {
        const climb = climbs[climbIndex];
          
          // Calculate actual distances for route coordinates using haversine formula
          // This matches the same logic used in ClimbHighlightLayer for consistency
          const routeDistances = [0]; // First point is at distance 0
          const R = 6371000; // Earth's radius in meters
          
          for (let i = 1; i < routeData.coordinates.length; i++) {
            const φ1 = routeData.coordinates[i - 1].lat * Math.PI / 180;
            const φ2 = routeData.coordinates[i].lat * Math.PI / 180;
            const Δφ = (routeData.coordinates[i].lat - routeData.coordinates[i - 1].lat) * Math.PI / 180;
            const Δλ = (routeData.coordinates[i].lng - routeData.coordinates[i - 1].lng) * Math.PI / 180;

            const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
                     Math.cos(φ1) * Math.cos(φ2) *
                     Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const segmentDistance = R * c;
            
            routeDistances.push(routeDistances[i - 1] + segmentDistance);
          }

          // Convert climb distances from km to meters for comparison
          const climbStartDistanceM = climb.startDistance * 1000;
          const climbEndDistanceM = climb.endDistance * 1000;
          
          // Find start coordinate index
          let startCoordIndex = 0;
          for (let i = 0; i < routeDistances.length; i++) {
            if (routeDistances[i] >= climbStartDistanceM) {
              startCoordIndex = Math.max(0, i - 1); // Take previous point to ensure coverage
              break;
            }
          }
          
          // Find end coordinate index
          let endCoordIndex = routeData.coordinates.length - 1;
          for (let i = startCoordIndex; i < routeDistances.length; i++) {
            if (routeDistances[i] >= climbEndDistanceM) {
              endCoordIndex = Math.min(routeData.coordinates.length - 1, i + 1); // Take next point to ensure coverage
              break;
            }
          }
          
          // Extract the coordinates for this climb segment
          const climbCoordinates = routeData.coordinates.slice(startCoordIndex, endCoordIndex + 1);
          
          if (climbCoordinates.length > 0) {
            // Convert to the format expected by fitMapToRoute
            const climbPoints = climbCoordinates.map(coord => ({ 
              lat: coord.lat, 
              lon: coord.lng 
            }));
            
            // Use the fitMapToRoute function to fit the climb with DetailPanel-aware padding
            if (fitMapToRouteRef.current) {
              fitMapToRouteRef.current(climbPoints);
            }
          }
        }
    }
  }, [routeData]);

  // Function to delete the current route and all points
  const handleDeleteRoute = useCallback(() => {
    // Set clearing flag to prevent any route loading
    isClearingMapRef.current = true;
    
    // IMMEDIATELY clear all potential sources of route reloading
    sessionStorage.removeItem('loadRoute'); // Clear sessionStorage route data
    hasLoadedFromUrlRef.current = true; // Prevent useEffect from loading anything
    needsAutoFitRef.current = false; // Reset auto-fit flag when clearing
    
    // First, immediately change the URL to prevent useEffect from reloading
    window.history.replaceState({}, '', '/');
    
    setPoints([]);
    setRouteData(null);
    setElevationData([]);
    setHighlightedClimbIndex(null); // Clear elevation highlighting when clearing map
    clearRouteSession(); // Clear the planning session when clearing the map
    clearEnhancedError(); // Clear any routing error state
    setHoverPoint(null);
    setStickyHoverPoint(null);
    setActiveStatType(null);
    setScrollToPoint(undefined);
    setCurrentRouteName(null);
    setCurrentRouteId(null);
    setCurrentRouteOwnerId(null);
    setCurrentRouteIsPublic(false);
    setCurrentRouteSourceType('manual'); // Reset to manual for new routes
    setHasUnsavedChanges(false); // No unsaved changes after discarding
    isRouteModifiedRef.current = false; // Reset modification flag
    
    // Also use React Router navigate for consistency
    navigate('/', { replace: true });
    
    // Reset flags after clearing state (allow fresh route loading later)
    setTimeout(() => {
      hasLoadedFromUrlRef.current = false;
      isClearingMapRef.current = false;
    }, 100); // Small delay to ensure all React updates complete
    
    // Reset undo/redo history when deleting the route
    resetHistory();
    
    // Clear planning session when route is deleted
    PlanningSessionManager.clearPlanningState();
  }, [navigate, resetHistory, clearRouteSession, clearEnhancedError]);

  const handleMarkerClick = useCallback((id: string) => {
    // Prevent marker interactions during loading
    if (isLoadingEnhanced) return;
    
    // Save current state before making changes
    saveToHistory();
    
    // Trigger delete animation
    setDeletingPointId(id);
    
    // After animation duration, actually delete the point
    setTimeout(() => {
      setPoints(prevPoints => {
        const newPoints = prevPoints.filter(point => point.id !== id);
        return newPoints.map((point, idx) => ({
          ...point,
          type: idx === 0 ? 'start' : idx === newPoints.length - 1 ? 'end' : 'via'
        }));
      });
      
      // Clear the deleting state
      setDeletingPointId(undefined);
      
      markRouteAsChanged(); // Route was modified by deleting a point
    }, 300); // Match animation duration
  }, [isLoadingEnhanced, saveToHistory, markRouteAsChanged]);

  const handleMapClick = useCallback((position: Position, pointType: 'via' | 'end' = 'via', fromSearch: boolean = false) => {
    if (isLoadingEnhanced) return;
    
    saveToHistory();
    
    if (!fromSearch) {
      lastPointViaSearchRef.current = false;
    }
    
    // Clear sticky hover when adding new points
    clearStickyHover();
    
    const newPoint: PointItem = {
      id: uuidv4(),
      type: 'via', // Will be updated in the type assignment logic
      position: position,
      address: '', // No displayName available from raw map click; will be reverse geocoded
      manuallyAdded: true // Mark as manually added
    };
    
    // Start geocoding immediately for the new point (force resolve for manually added points)
    startGeocodingForPoint(newPoint.id, position, true);
    
    // Only mark route as manual if we're starting a new route from scratch
    // Don't change the source type if we're adding points to an uploaded route
    // (This preserves the skip-geocoding behavior for uploaded route's middle points)
    if (currentRouteSourceType !== 'gpx_import' && currentRouteSourceType !== 'gpx_optimized') {
      setCurrentRouteSourceType('manual');
    }
    
    // Scroll to the new point if the distance panel is active
    if (activeStatType === 'distance') {
      setScrollToPoint(newPoint.id);
      // Clear the scroll target after a delay to allow the effect to run
      setTimeout(() => setScrollToPoint(undefined), 200);
    }
    
    setPoints(prevPoints => {
      let newPoints: PointItem[];
      if (pointType === 'end') {
        newPoints = [...prevPoints, newPoint];
        newPoints = newPoints.map((point, index) => ({
          ...point,
          type: (index === 0 ? 'start' : index === newPoints.length - 1 ? 'end' : 'via') as 'start' | 'via' | 'end'
        }));
      } else {
        const insertIndex = findNearestSegment(position, prevPoints);
        newPoints = [
          ...prevPoints.slice(0, insertIndex),
          newPoint,
          ...prevPoints.slice(insertIndex)
        ];
        newPoints = newPoints.map((point, index) => ({
          ...point,
          type: (index === 0 ? 'start' : index === newPoints.length - 1 ? 'end' : 'via') as 'start' | 'via' | 'end'
        }));
      }
      
      // Initialize route session when we reach 2 points (start of planning)
      if (prevPoints.length === 1 && newPoints.length === 2) {
        initializeRouteSession(newPoints.length);
      }
      
      // Initialize editing session when modifying a saved route
      if (currentRouteId && newPoints.length >= 2) {
        initializeRouteSession(newPoints.length, true); // Force editing session creation
      }
      
      return newPoints;
    });
    
    markRouteAsChanged(); // Route was modified
  }, [isLoadingEnhanced, saveToHistory, clearStickyHover, startGeocodingForPoint, markRouteAsChanged, initializeRouteSession, routeProfile]);

  // Separate handler for points added via location search
  const handleAddRoutePointViaSearch = useCallback((position: Position, pointType: 'via' | 'end' = 'via') => {
    if (isLoadingEnhanced) return;
    
    lastPointViaSearchRef.current = true;
    
    clearStickyHover();
    
    // Call the same logic as map click, but mark it as coming from search
    handleMapClick(position, pointType, true);
  }, [isLoadingEnhanced, handleMapClick, clearStickyHover]);

  const handlePointDelete = useCallback((id: string) => {
    // Trigger delete animation
    setDeletingPointId(id);
    
    // The route recalculation effect will see the deletingPointId and exclude it
    // This keeps the point visible for animation but excludes it from calculation
    
    // Actually remove the point after animation completes
    setTimeout(() => {
      setPoints(prevPoints => {
        const newPoints = prevPoints.filter(point => point.id !== id);
        return newPoints.map((point, idx) => ({
          ...point,
          type: (idx === 0 ? 'start' : idx === newPoints.length - 1 ? 'end' : 'via') as 'start' | 'end' | 'via'
        }));
      });
      setDeletingPointId(undefined);
      markRouteAsChanged(); // Route was modified
    }, 300); // Match animation duration
  }, [markRouteAsChanged]);

  const handleMarkerDrag = useCallback((id: string, newPosition: Position) => {
    // Prevent marker drag during loading
    if (isLoadingEnhanced) return;
    
    // Save current state before making changes
    saveToHistory();
    
    // Start geocoding immediately for the updated position (force resolve for manually moved points)
    startGeocodingForPoint(id, newPosition, true);
    
    // Don't immediately change route type to 'manual' - keep original source type
    // and only mark the individual point as manually modified. Route type will
    // transition to 'manual' when saving if it has manually modified points.
    
    setPoints(prevPoints => {
      const updatedPoints = prevPoints.map(point => 
        point.id === id 
          ? { ...point, position: newPosition, address: undefined, manuallyAdded: true } // Clear old address while geocoding, mark as manually modified
          : point
      );
      
      return updatedPoints;
    });
    
    markRouteAsChanged(); // Route was modified
    
    // Initialize editing session if modifying a saved route (force creation)
    if (currentRouteId) {
      initializeRouteSession(undefined, true); // Force editing session creation
    }
  }, [isLoadingEnhanced, saveToHistory, startGeocodingForPoint, markRouteAsChanged, currentRouteId, initializeRouteSession]);

  const handlePointsReorder = useCallback((result: DropResult) => {
    if (!result.destination) return;

    // Save current state before making changes
    saveToHistory();

    const sourceId = result.draggableId;
    const destinationIndex = result.destination.index;

    setPoints(prevPoints => {
      const pointToMove = prevPoints.find(p => p.id === sourceId);
      if (!pointToMove) return prevPoints;

      const newPoints = prevPoints.filter(p => p.id !== sourceId);
      newPoints.splice(destinationIndex, 0, pointToMove);
      
      const finalPoints = newPoints.map((point, index) => ({
        ...point,
        type: (index === 0 ? 'start' : index === newPoints.length - 1 ? 'end' : 'via') as 'start' | 'end' | 'via'
      }));
      
      return finalPoints;
    });
    
    markRouteAsChanged(); // Route was modified
    
    // Initialize editing session if modifying a saved route (force creation)
    if (currentRouteId) {
      initializeRouteSession(undefined, true); // Force editing session creation
    }
  }, [saveToHistory, markRouteAsChanged, currentRouteId, initializeRouteSession]);

  const handlePointEdit = useCallback((id: string, newPosition: { lat: number; lon: number }, displayName: string) => {
    // Save current state before making changes
    saveToHistory();
    
    const position: Position = { lat: newPosition.lat, lng: newPosition.lon };
    
    // Start geocoding for the updated position (force resolve for manually edited points)
    startGeocodingForPoint(id, position, true);
    
    // Don't immediately change route type to 'manual' - keep original source type
    // and only mark the individual point as manually modified. Route type will
    // transition to 'manual' when saving if it has manually modified points.
    
    setPoints(prevPoints => {
      const updatedPoints = prevPoints.map(point => 
        point.id === id 
          ? { 
              ...point, 
              position, 
              address: displayName, // Use the display name from search as initial address
              manuallyAdded: true // Mark as manually modified
            } 
          : point
      );
      
      return updatedPoints;
    });
    
    markRouteAsChanged(); // Route was modified
    
    // Initialize editing session if modifying a saved route (force creation)
    if (currentRouteId) {
      initializeRouteSession(undefined, true); // Force editing session creation
    }
  }, [saveToHistory, startGeocodingForPoint, markRouteAsChanged, currentRouteId, initializeRouteSession]);

  const handlePointInsert = useCallback((insertIndex: number, newPosition: { lat: number; lon: number }, displayName: string) => {
    // Save current state before making changes
    saveToHistory();
    
    const position: Position = { lat: newPosition.lat, lng: newPosition.lon };
    
    const newPoint: PointItem = {
      id: uuidv4(),
      type: 'via', // Will be updated in the type assignment logic
      position: position,
      address: displayName, // Use the display name from search as initial address
      manuallyAdded: true // Mark as manually added
    };
    
    // Start geocoding for the new point (force resolve for manually added points)
    startGeocodingForPoint(newPoint.id, position, true);
    
    // Only mark route as manual if we're starting a new route from scratch
    // Don't change the source type if we're adding points to an uploaded route
    // (This preserves the skip-geocoding behavior for uploaded route's middle points)
    if (currentRouteSourceType !== 'gpx_import' && currentRouteSourceType !== 'gpx_optimized') {
      setCurrentRouteSourceType('manual');
    }
    
    // Scroll to the new point if the distance panel is active
    if (activeStatType === 'distance') {
      setScrollToPoint(newPoint.id);
      // Clear the scroll target after a delay to allow the effect to run
      setTimeout(() => setScrollToPoint(undefined), 200);
    }
    
    setPoints(prevPoints => {
      // Insert the new point at the specified index
      const newPoints = [
        ...prevPoints.slice(0, insertIndex),
        newPoint,
        ...prevPoints.slice(insertIndex)
      ];
      
      // Reassign types based on position
      return newPoints.map((point, index) => ({
        ...point,
        type: index === 0 ? 'start' : index === newPoints.length - 1 ? 'end' : 'via'
      }));
    });
    
    markRouteAsChanged(); // Route was modified
  }, [saveToHistory, startGeocodingForPoint, activeStatType, markRouteAsChanged]);

  // Handler for manual geocoding trigger from RoutePoint component
  const handleManualGeocode = useCallback(async (pointId: string, position: { lat: number; lon: number }) => {
    // Add point to loading set
    setGeocodingLoadingPoints(prev => new Set([...prev, pointId]));
    
    try {
      // Get geocode result directly to persist to database
      const geocodeResult = await reverseGeocode({ lat: position.lat, lng: position.lon });
      const address = formatGeocodeResult(geocodeResult);
      
      // Update the point with both formatted address and structured data
      setPoints(prevPoints => 
        prevPoints.map(point => 
          point.id === pointId 
            ? { ...point, address, geocodeResult }
            : point
        )
      );
      
      // If we have a saved route, immediately persist the geocoded address to database
      if (currentRouteId && storageManagerRef.current) {
        try {
          // Find the point to get its database ID
          const targetPoint = points.find(p => p.id === pointId);
          if (targetPoint?.dbId) {
            // Convert geocode result to address fields for database storage
            const addressFields = geocodeResultToAddressFields(geocodeResult);
            
            // Update only the address fields in the database using the database ID
            await storageManagerRef.current.updateRoutePoint(
              currentRouteId, 
              targetPoint.dbId, 
              addressFields
            );
            
          }
        
        } catch (error) {
          console.warn('Error persisting geocoded address to database:', error);
          // Don't throw - geocoding succeeded, persistence is just a bonus
        }
      }
    } catch (error) {
      console.warn('Failed to resolve address for point:', pointId, error);
    } finally {
      // Remove point from loading set
      setGeocodingLoadingPoints(prev => {
        const newSet = new Set(prev);
        newSet.delete(pointId);
        return newSet;
      });
    }
  }, [currentRouteId, points]);

  React.useEffect(() => {
    // Clear ALL highlights when starting a new route calculation
    setHighlightedClimbIndex(null);
    setHighlightedSurface(null);
    setHighlightedSegmentIndex(0);
    
    // Skip if manual retry is in progress
    if (isManualRetryRef.current) {
      return;
    }
    
    // Skip if we're restoring from error state (already have the route data)
    if (isRestoringFromErrorRef.current) {
      return;
    }
    
    // Don't calculate route during deletion animation
    if (deletingPointId) {
      routeCalculationRef.current.isCalculating = false;
      return;
    }
    
    const pointsForCalculation = points;
        
    // For initial load with no points, just clear state
    if (pointsForCalculation.length < 2) {
      // Don't clear route data if we're currently restoring session (prevents clearing during restoration)
      if (isRestoringSessionRef.current) {
        return;
      }
      
      // Don't clear loading state if we're currently loading a route
      if (loadingState === 'calculating') {
        return;
      }
      
      setRouteData(null);
      setElevationData([]);
      setHighlightedClimbIndex(null); // Clear elevation highlighting when no route
      setHasRoutingError(false);
      transitionLoadingState('idle', 'no points to calculate');
      routeCalculationRef.current.isCalculating = false;
      return;
    }

    // Check if this is the same calculation to avoid duplicates
    const positions = pointsForCalculation.map(p => p.position).filter((p): p is Position => p !== null);
    const calculationKey = `${positions.map(p => `${p.lat},${p.lng}`).join('|')}-${routeProfile}`;
    
    // Skip calculation if we've already calculated this exact route (regardless of isCalculating flag)
    if (routeCalculationRef.current.lastCalculationKey === calculationKey) {
      return;
    }

    // Note: Don't save history here! History should be saved by user actions (handleMapClick, etc.)
    // before they modify points. Saving here would overwrite the pre-modification state.

    // Mark as calculating to prevent duplicates
    routeCalculationRef.current.isCalculating = true;
    routeCalculationRef.current.lastCalculationKey = calculationKey;
    routeCalculationRef.current.wasCancelled = false; // Reset cancellation flag for new calculation
    
    // Set legacy loading state for backward compatibility
    transitionLoadingState('calculating', 'starting enhanced route calculation');
    
    // Get configuration for this route
    const config = routingConfig.getConfigForRoute(positions, routeProfile);
    
    // Start enhanced route calculation
    calculateEnhancedRoute(positions, routeProfile, config.timeout, routeSessionId, currentRouteId);

  }, [
    points, 
    routeProfile, 
    deletingPointId
  ]);

  const handleHover = useCallback((index: number | null) => {
    setHoverPoint(index);
    // Update sticky hover point when we have a valid hover
    if (index !== null) {
      setStickyHoverPoint(index);
    }
    // Don't clear sticky hover when index is null (mouse leave)
    // The sticky hover will only be cleared by explicit actions
  }, []);

  // Use sticky hover point when hover point is null
  const activeHoverPoint = hoverPoint !== null ? hoverPoint : stickyHoverPoint;

  const handleStatClick = useCallback((statType: StatType) => {
    const prevStat = activeStatType; // current before change

    // Surface highlight clearing logic mirroring climb behavior
    const wasSurfaceBeforeCollapse = !activeStatType && lastActiveStatRef.current === 'surface';
    const switchingAwayFromSurface = prevStat === 'surface' && statType && statType !== 'surface';
    if (switchingAwayFromSurface || (wasSurfaceBeforeCollapse && statType && statType !== 'surface')) {
      setHighlightedSurface(null);
      setHighlightedSegmentIndex(0);
    }

    // Climb highlight logic
    const wasAscendBeforeCollapse = !activeStatType && lastActiveStatRef.current === 'ascend';
    const switchingAwayFromAscend = prevStat === 'ascend' && statType && statType !== 'ascend';
    if (switchingAwayFromAscend || (wasAscendBeforeCollapse && statType && statType !== 'ascend')) {
      setHighlightedClimbIndex(null);
    }

    if (statType === null) {
      // Collapsing: remember what was active
      lastActiveStatRef.current = prevStat;
      setIsDetailPanelExpanded(false);
    } else {
      // Opening or switching: update last active reference
      lastActiveStatRef.current = statType;
    }

    setActiveStatType(statType);
  }, [activeStatType]);

  const handleDragChange = useCallback((isDragActive: boolean, offset: number) => {
    
    setIsDragging(isDragActive);
    
    // If drag ended, determine final state based on drag distance
    if (!isDragActive) {
      const threshold = 40; // pixels threshold for state change (reduced from 80px for quicker transitions)
      
      // Set transitioning state to prevent transform during state changes
      setIsTransitioning(true);
      setDragOffset(0); // Reset offset immediately
      
      if (Math.abs(offset) > threshold) {
        if (offset < 0) {
          // Dragged up significantly - expand to fullscreen (Scenario B -> C)
          if (activeStatType && !isDetailPanelExpanded) {
            setIsDetailPanelExpanded(true);
          }
        } else {
          // Dragged down significantly
          if (activeStatType && isDetailPanelExpanded) {
            // Collapse from fullscreen to regular (Scenario C -> B)
            setIsDetailPanelExpanded(false);
          } else if (activeStatType && !isDetailPanelExpanded) {
            // Close panel completely (Scenario B -> A)
            setActiveStatType(null);
          }
        }
      } else {
        // Drag didn't exceed threshold, just reset
        setDragOffset(0);
      }
      
      // Clear transitioning state after animation completes
      setTimeout(() => {
        setIsTransitioning(false);
      }, 150); // Match the transition duration
    } else {
      // During drag, update offset normally
      if (!isTransitioning) {
        setDragOffset(offset);
      } 
    }
  }, [activeStatType, isDetailPanelExpanded, isTransitioning, dragOffset]);

  const handleDownload = useCallback(() => {
    if (routeData && points.length >= 2) {
      setSaveModalOpen(true);
    }
  }, [routeData, points]);

  // Function to handle GPX download from the modal
  const handleGpxDownload = useCallback(async () => {
    if (routeData && points.length >= 2) {
      try {
        await downloadRouteAsGPX(
          currentRouteId, // Pass route ID for stored data optimization
          points.filter(p => p.position).map(p => p.position!),
          routeProfile,
          currentRouteName || undefined,
          () => setDownloadModalOpen(true),  // onLoadingStart
          () => setDownloadModalOpen(false),  // onLoadingEnd
          { getAccessToken, refreshSession }   // sessionFunctions
        );
        
      } catch (error) {
        setDownloadModalOpen(false); // Ensure modal is closed on error
        console.error('Failed to download GPX:', error);
        // You can add error toast here if needed
      }
    }
  }, [routeData, points, routeProfile, currentRouteId, currentRouteName, getAccessToken, refreshSession]);

  // Function to validate route name for duplicates
  const handleValidateRouteName = useCallback(async (routeName: string): Promise<boolean> => {
    if (!storageManagerRef.current || !user) {
      return true; // Don't validate if not authenticated or storage not ready
    }

    try {
      // For validation, we need to determine if this is for updating the current route
      // or creating a new one. If the name is different from currentRouteName,
      // or if we're in "Save as New Route" mode, don't exclude the current route ID
      const shouldExcludeCurrentRoute = currentRouteName && routeName === currentRouteName;
      const excludeRouteId = shouldExcludeCurrentRoute ? (currentRouteId || undefined) : undefined;
      
      const result = await storageManagerRef.current.checkRouteNameExists(
        getUserId(),
        routeName,
        excludeRouteId
      );
      
      if (!result.success) {
        console.error('Failed to validate route name:', result.error);
        return true; // Allow save if validation fails
      }
      
      return !result.data; // Return true if name is valid (doesn't exist)
    } catch (error) {
      console.error('Error validating route name:', error);
      return true; // Allow save if validation fails
    }
  }, [user, currentRouteId, currentRouteName]);

  // Function to handle overwrite save (for existing loaded routes)
  const handleOverwriteSave = useCallback(async () => {
    if (!currentRouteId || !currentRouteName || !routeData || points.length < 2) {
      console.error('Cannot overwrite: missing route information');
      toast.routeSaveError('Cannot update route: missing route information');
      return;
    }
    
    if (!user) {
      console.error('User must be authenticated to save routes');
      toast.routeSaveError('You must be signed in to save routes');
      return;
    }
    
    if (!storageManagerRef.current) {
      console.error('Storage manager not initialized');
      toast.routeSaveError('Unable to save route. Please try again.');
      return;
    }

    // Set saving state
    setIsSavingRoute(true);

    try {
      // Convert route points to storage format
      const routePoints = points
        .filter(point => point.position)
        .map((point, index) => ({
          lat: point.position!.lat,
          lng: point.position!.lng,
          orderIndex: index,
          // Include address information if available (to avoid repeated API calls)
          ...geocodeResultToAddressFields(point.geocodeResult)
        }));
      
      // Determine the appropriate source type for saving (same logic as new route save)
      // For GPX uploads with manual modifications, use 'gpx_optimized' to preserve GPX origin
      const hasManuallyModifiedPoints = points.some(point => point.manuallyAdded);
      const finalSourceType = (() => {
        if (hasManuallyModifiedPoints) {
          // If route started as GPX import, mark as optimized to preserve GPX origin
          if (currentRouteSourceType === 'gpx_import' || currentRouteSourceType === 'gpx_optimized') {
            return 'gpx_optimized';
          }
          // If route started as manual, keep it manual
          return 'manual';
        }
        // No manual modifications, keep original source type
        return currentRouteSourceType;
      })();
      
      const routeUpdate = {
        name: currentRouteName,
        bikeProfile: routeProfile,
        points: routePoints,
        sourceType: finalSourceType,
        distance: routeData?.distance,
        duration: routeData?.duration,
        ascend: routeData?.ascend,
        // Include power calculator settings
        powerToWeight: powerToWeight,
        showOriginal: showOriginal
      };
      
      const result = await storageManagerRef.current.updateRoute(currentRouteId, routeUpdate);
      
      if (result.success) {
        
        // Update current route source type to reflect what was actually saved
        setCurrentRouteSourceType(finalSourceType);
        
        // Reset undo/redo history after successful update
        resetHistory();
        
        // Save full route data after successful update
        if (routeData) {
          try {
            await saveFullRouteData(currentRouteId, routeData, routeProfile, undefined, { getAccessToken, refreshSession });
          } catch (fullDataError) {
            console.warn('Failed to save full route data (route updated successfully):', fullDataError);
          }
        }
        
        // Only after full route data is saved, clear the unsaved changes flag
        setHasUnsavedChanges(false);
        isRouteModifiedRef.current = false; // Reset modification flag
        
        // Blur any focused element before closing to avoid aria-hidden focus trap
        blurActiveElement();
        
        // Use requestAnimationFrame to ensure blur completes before panel closes
        requestAnimationFrame(() => {
          // Close the detail panel completely after successful save
          setActiveStatType(null);
          setIsDetailPanelExpanded(false);
        });
        
        // Generate and upload thumbnails after successful update
        try {
          const thumbnails = await generateAndUploadThumbnails(currentRouteId);
          const updateData: any = {};
          
          if (thumbnails.routeThumbnail) {
            updateData.thumbnail_path = thumbnails.routeThumbnail;
          }
          
          if (thumbnails.elevationThumbnail) {
            updateData.elevation_thumbnail_path = thumbnails.elevationThumbnail;
          }
          
          if (Object.keys(updateData).length > 0) {
            await storageManagerRef.current.updateRoute(currentRouteId, updateData);
          }
        } catch (thumbnailError) {
          console.warn('Failed to generate thumbnails (route updated successfully):', thumbnailError);
        }
        
        toast.routeUpdated(currentRouteName);
      } else {
        console.error('Failed to update route:', result.error);
        toast.routeSaveError(result.error || 'Failed to update route');
      }
    } catch (error) {
      console.error('Error updating route:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update route';
      toast.routeSaveError(errorMessage);
    } finally {
      // Always clear saving state
      setIsSavingRoute(false);
    }
  }, [currentRouteId, currentRouteName, routeData, points, routeProfile, user, storageManagerRef, generateAndUploadThumbnail, toast, resetHistory, powerToWeight, showOriginal]);

  // Function to handle route saving
  const handleRouteSave = useCallback(async (routeName: string) => {
    // Check if user is authenticated
    if (!user) {
      console.error('User must be authenticated to save routes');
      toast.routeSaveError('You must be signed in to save routes');
      return;
    }
    
    if (!storageManagerRef.current || points.length < 2) {
      console.error('Storage manager not initialized or insufficient route points');
      toast.routeSaveError('Unable to save route. Please ensure you have at least 2 route points.');
      return;
    }

    // Set saving state
    setIsSavingRoute(true);

    try {
      // Convert route points (waypoints) to the storage format - include geocoding data to avoid API calls
      const routePoints = points
        .filter(point => point.position) // Only include points with valid positions
        .map((point, index) => ({
          lat: point.position!.lat,
          lng: point.position!.lng,
          orderIndex: index,
          // Include address information if available (to avoid repeated API calls)
          ...geocodeResultToAddressFields(point.geocodeResult)
        }));
      
      // Check for duplicate route names when creating a new route
      const duplicateCheck = await storageManagerRef.current.checkRouteNameExists(
        getUserId(), 
        routeName
      );
      
      if (!duplicateCheck.success) {
        console.error('Failed to check for duplicate route names:', duplicateCheck.error);
        toast.routeSaveError('Failed to check for duplicate route names');
        return;
      }
      
      if (duplicateCheck.data) {
        console.error('Route name already exists for this user:', routeName);
        toast.routeSaveError(`A route named "${routeName}" already exists`);
        return;
      }
      
      // Create new route
      
      // Determine the appropriate source type for saving
      // For GPX uploads with manual modifications, use 'gpx_optimized' to preserve GPX origin
      // Only transition to 'manual' for routes that started as manual
      const hasManuallyModifiedPoints = points.some(point => point.manuallyAdded);
      const finalSourceType = (() => {
        if (hasManuallyModifiedPoints) {
          // If route started as GPX import, mark as optimized to preserve GPX origin
          if (currentRouteSourceType === 'gpx_import' || currentRouteSourceType === 'gpx_optimized') {
            return 'gpx_optimized';
          }
          // If route started as manual, keep it manual
          return 'manual';
        }
        // No manual modifications, keep original source type
        return currentRouteSourceType;
      })();

      const routeToSave = {
        name: routeName,
        bikeProfile: routeProfile,
        userId: getUserId(),
        points: routePoints,
        sourceType: finalSourceType,
        // Include route statistics if available
        distance: routeData?.distance,
        duration: routeData?.duration,
        ascend: routeData?.ascend,
        // Include power calculator settings
        powerToWeight: powerToWeight,
        showOriginal: showOriginal
      };
      
      const result = await storageManagerRef.current.saveRoute(routeToSave);
      
      if (result.success) {
        
        // Update current route source type to reflect what was actually saved
        setCurrentRouteSourceType(finalSourceType);
        
        // Update current route name and ID to reflect the saved route
        setCurrentRouteName(routeName);
        const savedRouteId = result.data?.id;
        if (savedRouteId) {
          setCurrentRouteId(savedRouteId);
          
          // Save full route data after successful route save
          if (routeData) {
            try {
              await saveFullRouteData(savedRouteId, routeData, routeProfile, undefined, { getAccessToken, refreshSession });
            } catch (fullDataError) {
              console.warn('Failed to save full route data (route saved successfully):', fullDataError);
            }
          }
          
          // Generate and upload thumbnails after successful route save
          try {
            const thumbnails = await generateAndUploadThumbnails(savedRouteId);
            const updateData: any = {};
            
            if (thumbnails.routeThumbnail) {
              updateData.thumbnail_path = thumbnails.routeThumbnail;
            }
            
            if (thumbnails.elevationThumbnail) {
              updateData.elevation_thumbnail_path = thumbnails.elevationThumbnail;
            }
            
            if (Object.keys(updateData).length > 0) {
              await storageManagerRef.current.updateRoute(savedRouteId, updateData);
            }
          } catch (thumbnailError) {
            console.warn('Failed to generate thumbnails (route saved successfully):', thumbnailError);
            // Don't fail the whole operation if thumbnail generation fails
          }
        }
        
        setHasUnsavedChanges(false); // No unsaved changes after successful save
        isRouteModifiedRef.current = false; // Reset modification flag
        
        // Blur any focused element before closing to avoid aria-hidden focus trap
        blurActiveElement();
        
        // Use requestAnimationFrame to ensure blur completes before panel closes
        requestAnimationFrame(() => {
          // Close the detail panel completely after successful save
          setActiveStatType(null);
          setIsDetailPanelExpanded(false);
        });
        
        // Reset undo/redo history after successful save
        resetHistory();
        
        // Show success notification to user
        toast.routeSaved(routeName);
        
      } else {
        console.error('Failed to save route:', result.error);
        // Show error notification to user
        toast.routeSaveError(result.error || 'Failed to save route');
      }
    } catch (error) {
      console.error('Error saving route:', error);
      // Show error notification to user
      toast.routeSaveError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      // Always clear saving state
      setIsSavingRoute(false);
    }
  }, [routeData, routeProfile, user, points, currentRouteId, currentRouteName, generateAndUploadThumbnail, storageManagerRef, toast, resetHistory]);

  // Function to actually load a route (called after confirmation if needed)
  const loadRouteDirectly = useCallback(async (routeId: string) => {
    // Prevent duplicate executions in development (React Strict Mode)
    const executionKey = `loading-${routeId}`;
    if (sessionStorage.getItem(executionKey) === 'true') {
      return;
    }
    sessionStorage.setItem(executionKey, 'true');
        
    // Set loading state when starting to load the route
    transitionLoadingState('calculating', 'loading route from storage');
    
    // Add minimum loading duration to ensure spinner is visible
    const startTime = Date.now();
    const minLoadingDuration = 500; // 500ms minimum to show spinner
    
    if (!storageManagerRef.current) {
      sessionStorage.removeItem(executionKey);
      transitionLoadingState('idle', 'no storage manager available');
      return;
    }

    try {
      // Clear route cache to prevent old cached route data from persisting
      clearRouteCache();
      
      // Clear proxy cache to prevent stale BRouter responses
      // This is the KEY fix - the proxy was caching BRouter responses for 30 minutes
      await clearProxyCache();
      
      // // // Clear ALL session storage related to route planning to prevent interference
      // This is important because session storage might contain temporary route modifications
      sessionStorage.removeItem('loadRoute');
      PlanningSessionManager.clearPlanningSession();
      
      const result = await storageManagerRef.current.getRoute(routeId);
            
      if (result.success && result.data) {
        const route = result.data;
        
        // Set the route source type from loaded data
        setCurrentRouteSourceType(route.sourceType || 'manual');
        
        // Convert stored route points back to PointItems, using stored address data to avoid API calls
        const newPoints: PointItem[] = route.points
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map((point, index) => {
            // Get stored address information if available
            const storedGeocodeResult = addressFieldsToGeocodeResult(point);
            const formattedAddress = formatAddressFromRoutePoint(point);
            
            return {
              id: uuidv4(), // In-memory UUID for React key and UI operations
              position: { lat: point.lat, lng: point.lng },
              type: index === 0 ? 'start' : index === route.points.length - 1 ? 'end' : 'via',
              address: formattedAddress, // Use stored address data instead of undefined
              geocodeResult: storedGeocodeResult, // Include structured geocoding data
              orderIndex: index,
              // For GPX-origin routes, mark original points as not manually added
              // This preserves the API optimization for uploaded route points
              manuallyAdded: false,
              dbId: point.id // Preserve database RoutePoint ID for persistence operations
            };
          });

        // Update route profile if different
        if (route.bikeProfile !== routeProfile) {
          setRouteProfile(route.bikeProfile as RouteProfile);
        }

        // Try to load route data from stored full route data BEFORE setting points
        const positions: Position[] = newPoints
          .map(point => point.position)
          .filter((pos): pos is Position => pos !== null);
          
        const loadedRouteData = await loadRouteWithFullData(
          routeId, 
          positions, 
          route.bikeProfile as RouteProfile,
          { getAccessToken, refreshSession }
        );
        
        if (loadedRouteData) {
          // Successfully loaded from stored data - inject directly into route processing pipeline!
          
          // Calculate the route key and set it BEFORE setting points to prevent useEffect from triggering API call
          const calculationKey = `${positions.map(p => `${p.lat},${p.lng}`).join('|')}-${route.bikeProfile}`;
          routeCalculationRef.current.lastCalculationKey = calculationKey;
          routeCalculationRef.current.isCalculating = false;
          
          // Ensure minimum loading duration for better UX
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingDuration - elapsedTime);
          
          if (remainingTime > 0) {
            // Wait for minimum duration before completing loading
            setTimeout(() => {
              // Mark that we need to auto-fit the map to the loaded route
              needsAutoFitRef.current = true;
              
              // Set points first
              setPoints(newPoints);
              
              // Inject stored route data directly into the completion handler
              // This skips the API call entirely and processes the route as if it came from BRouter
              handleRoutingComplete(loadedRouteData);
            }, remainingTime);
          } else {
            // Mark that we need to auto-fit the map to the loaded route
            needsAutoFitRef.current = true;
            
            // Set points first
            setPoints(newPoints);
            
            // Inject stored route data directly into the completion handler
            // This skips the API call entirely and processes the route as if it came from BRouter
            handleRoutingComplete(loadedRouteData);
          }
        } else {
          // No stored data - set points which will trigger automatic route calculation
          // The loading state will be handled by the route calculation useEffect
          
          // Ensure minimum loading duration for better UX
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingDuration - elapsedTime);
          
          if (remainingTime > 0) {
            setTimeout(() => {
              // Mark that we need to auto-fit the map after route calculation
              needsAutoFitRef.current = true;
              
              setPoints(newPoints);
              setRouteData(null);
              setElevationData([]);
            }, remainingTime);
          } else {
            // Mark that we need to auto-fit the map after route calculation
            needsAutoFitRef.current = true;
            
            setPoints(newPoints);
            setRouteData(null);
            setElevationData([]);
          }
          
          // Keep loading state active as route calculation will take over
          // transitionLoadingState will be called by the route calculation process
        }
        
        // Clear any cached route data to prevent old polyline from showing
        // Force clear planning session with old route data
        PlanningSessionManager.clearPlanningSession();
        
        // Clear any sticky UI state that might show old route
        setHoverPoint(null);
        setStickyHoverPoint(null);
        setHighlightedSurface(null);
        setHighlightedSegmentIndex(0);
        
        // Set the current route name and ID for display
        setCurrentRouteName(route.name);
        setCurrentRouteId(route.id || null);
        setCurrentRouteOwnerId(route.userId || null);
        setCurrentRouteIsPublic(route.isPublic || false);
        clearRouteSession(); // Clear planning session when loading saved route
        
        // Restore power calculator settings if available
        if (route.powerToWeight !== undefined) {
          console.log('📊 Restoring power settings:', { powerToWeight: route.powerToWeight, showOriginal: route.showOriginal });
          setPowerToWeight(route.powerToWeight);
          localStorage.setItem('cyclingbuddy_power_to_weight', route.powerToWeight.toString());
        }
        if (route.showOriginal !== undefined) {
          setShowOriginal(route.showOriginal);
          localStorage.setItem('cyclingbuddy_duration_auto_mode', route.showOriginal.toString());
        }
        
        // Update URL to clean path-based route sharing (/route/123)
        if (route.id && window.location.pathname !== `/route/${route.id}`) {
          navigate(`/route/${route.id}`, { replace: true });
        }
        
        setHasUnsavedChanges(false); // No changes initially when loading
        isRouteModifiedRef.current = false; // Reset modification flag
        
        // Reset undo/redo history when loading a new route
        resetHistory();
        
        // Close the modal
        setSaveModalOpen(false);
        
        // Note: isLoadingSavedRoute flag is cleared after map fit in the success case above
      } else {
        console.error('Failed to load route:', result.error);
        // Show error notification to user
        toast.routeLoadError(result.error || 'Failed to load route');
        
        // Ensure minimum loading duration even for errors
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingDuration - elapsedTime);
        
        if (remainingTime > 0) {
          setTimeout(() => {
            transitionLoadingState('idle', 'route loading failed');
          }, remainingTime);
        } else {
          transitionLoadingState('idle', 'route loading failed');
        }
      }
    } catch (error) {
      console.error('Error loading route:', error);
      // Show error notification to user
      toast.routeLoadError(error instanceof Error ? error.message : 'An unexpected error occurred');
      
      // Ensure minimum loading duration even for errors
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingDuration - elapsedTime);
      
      if (remainingTime > 0) {
        setTimeout(() => {
          transitionLoadingState('idle', 'route loading error');
        }, remainingTime);
      } else {
        transitionLoadingState('idle', 'route loading error');
      }
    } finally {
      // Clean up execution tracking
      sessionStorage.removeItem(executionKey);
    }
  }, [routeProfile, mapCenter, mapZoom, resetHistory, transitionLoadingState]);

  // Function to handle loading a saved route (simplified - no conflict checking here)
  const handleLoadRoute = useCallback(async (routeId: string) => {
    if (!storageManagerRef.current) return;
    // Load directly since conflict checking is now handled in MyRoutesPage
    await loadRouteDirectly(routeId);
  }, [loadRouteDirectly]);

  // Function to undo last change
  const handleUndo = useCallback(() => {
    if (routeHistory.length === 0) {
      return;
    }

    const lastState = routeHistory[routeHistory.length - 1];
    
    // Save current state to redo history before undoing
    setRedoHistory(prev => [...prev, {
      points: [...points],
      routeData: routeData ? { ...routeData } : null,
      elevationData: [...elevationData]
    }].slice(-10)); // Keep only last 10 redo states
    
    setPoints(lastState.points);
    setRouteData(lastState.routeData);
    setElevationData(lastState.elevationData);
    
    // Remove the last state from history
    setRouteHistory(prev => prev.slice(0, -1));
    
    // Clear error state if undoing to a state with fewer than 2 points (no route possible)
    if (lastState.points.length < 2) {
      clearEnhancedError();
    }
    
    // Clear UI state
    setHoverPoint(null);
    setStickyHoverPoint(null);
    setScrollToPoint(undefined);
    setCurrentRouteName(null); // Clear route name as we're restoring previous state
    setCurrentRouteId(null); // Clear route ID as we're restoring previous state
    setCurrentRouteOwnerId(null); // Clear route owner ID as we're restoring previous state
    setCurrentRouteIsPublic(false); // Clear public status as we're restoring previous state
  }, [routeHistory, points, routeData, elevationData, clearEnhancedError]);

  // Function to redo last undone change
  const handleRedo = useCallback(() => {
    if (redoHistory.length === 0) {
      return;
    }

    const nextState = redoHistory[redoHistory.length - 1];
    
    // Save current state to undo history before redoing
    setRouteHistory(prev => [...prev, {
      points: [...points],
      routeData: routeData ? { ...routeData } : null,
      elevationData: [...elevationData]
    }].slice(-10)); // Keep only last 10 undo states
    
    setPoints(nextState.points);
    setRouteData(nextState.routeData);
    setElevationData(nextState.elevationData);
    
    // Remove the last state from redo history
    setRedoHistory(prev => prev.slice(0, -1));
    
    // Clear UI state
    setHoverPoint(null);
    setStickyHoverPoint(null);
    setScrollToPoint(undefined);
    setCurrentRouteName(null); // Clear route name as we're restoring previous state
    setCurrentRouteId(null); // Clear route ID as we're restoring previous state
    setCurrentRouteOwnerId(null); // Clear route owner ID as we're restoring previous state
    setCurrentRouteIsPublic(false); // Clear public status as we're restoring previous state
  }, [redoHistory, points, routeData, elevationData]);

  // Wrapper for cancel that restores previous state
  const handleCancelWithUndo = useCallback(() => {
    // Set flag to ignore any pending route calculation results
    routeCalculationRef.current.wasCancelled = true;
    
    // Set flag to prevent useEffect from triggering recalculation during restoration
    isRestoringFromErrorRef.current = true;
    
    // Cancel the calculation FIRST to stop the background request
    const wasCancelled = cancelEnhancedCalculation();
    
    if (wasCancelled) {
      // Then restore previous state from history
      if (routeHistory.length > 0) {
        const lastState = routeHistory[routeHistory.length - 1];
        
        // Use flushSync to ensure React updates synchronously
        flushSync(() => {
          // Restore the previous state
          setPoints(lastState.points);
          setRouteData(lastState.routeData);
          setElevationData(lastState.elevationData);
          
          // Remove the last state from history since we're restoring it
          setRouteHistory(prev => prev.slice(0, -1));
          
          // Clear error state if restoring to a state with fewer than 2 points
          if (lastState.points.length < 2) {
            clearEnhancedError();
          }
        });
      }
    }
    
    // Reset flag after state updates complete
    setTimeout(() => {
      isRestoringFromErrorRef.current = false;
    }, 100);
  }, [cancelEnhancedCalculation, routeHistory, clearEnhancedError]);

  // Handler for "Undo" from error state - restores to previous successful state (same as cancel)
  const handleUndoFromError = useCallback(() => {
    // Clear the error first
    clearEnhancedError();
    
    // Set flag to prevent useEffect from triggering recalculation
    isRestoringFromErrorRef.current = true;
    
    // Restore previous state from history (before the failed calculation)
    if (routeHistory.length > 0) {
      const lastState = routeHistory[routeHistory.length - 1];
      
      // Restore the previous state
      setPoints(lastState.points);
      setRouteData(lastState.routeData);
      setElevationData(lastState.elevationData);
      
      // Remove the last state from history since we're restoring it
      setRouteHistory(prev => prev.slice(0, -1));
    } else {
      // No history - just clear points if we only had 2 points initially
      setRouteData(null);
      setElevationData([]);
    }
    
    // Reset flag after state updates complete
    setTimeout(() => {
      isRestoringFromErrorRef.current = false;
    }, 100);
  }, [clearEnhancedError, routeHistory]);

  // Function to reverse the route direction
  const handleReverseRoute = useCallback(() => {
    if (points.length < 2) {
      return; // Can't reverse route with less than 2 points
    }

    // Save current state to undo history before reversing
    setRouteHistory(prev => [...prev, {
      points: [...points],
      routeData: routeData ? { ...routeData } : null,
      elevationData: [...elevationData]
    }].slice(-10)); // Keep only last 10 undo states

    // Clear redo history when making a new change
    setRedoHistory([]);

    // Reverse the points array (A->B->C->D becomes D->C->B->A)
    const reversedPoints = [...points].reverse().map((point, index, array) => {
      // Update point types when reversing: start becomes end, end becomes start, via stays via
      let newType: 'start' | 'via' | 'end' = point.type;
      
      if (index === 0) {
        // First point in reversed array should be 'start'
        newType = 'start';
      } else if (index === array.length - 1) {
        // Last point in reversed array should be 'end'
        newType = 'end';
      } else {
        // Middle points remain 'via'
        newType = 'via';
      }
      
      return {
        ...point,
        type: newType
      };
    });
    setPoints(reversedPoints);

    // Clear route data and elevation data - they will be recalculated
    setRouteData(null);
    setElevationData([]);

    // Clear UI state
    setHoverPoint(null);
    setStickyHoverPoint(null);
    setScrollToPoint(undefined);
    
    // Trigger route calculation with reversed points
    transitionLoadingState('calculating', 'route reversal');
    setHasRoutingError(false);
  }, [points, routeData, elevationData, transitionLoadingState]);

  // Function to reset route to the saved version
  const handleResetRoute = useCallback(async () => {
    if (!currentRouteId) {
      console.warn('No current route ID available for reset');
      return;
    }

    try {
      await handleLoadRoute(currentRouteId);
      // Show success notification to user
      toast.routeLoaded(currentRouteName || 'Route');
    } catch (error) {
      console.error('Error resetting route:', error);
      // Show error notification to user
      toast.routeLoadError(error instanceof Error ? error.message : 'Failed to reset route');
    }
  }, [currentRouteId, handleLoadRoute, currentRouteName, toast]);

  // Clear surface highlighting and reset expanded state when detail panel is closed
  useEffect(() => {
    if (!activeStatType) {
      // Do not clear surface highlight here; persistence across collapse is desired.
      setIsDetailPanelExpanded(false); // Still reset expanded state when panel is closed
    }
  }, [activeStatType]);

  // Handle clearing surface highlighting
  const handleClearSurfaceHighlight = useCallback(() => {
    setHighlightedSurface(null);
    setHighlightedSegmentIndex(0);
  }, []);

  // Check for route ID in URL path and sessionStorage and load it
  useEffect(() => {
    // Don't load anything if we're in the middle of clearing the map
    if (isClearingMapRef.current) {
      return;
    }

    // Only run once per URL route ID or on mount if no URL route ID
    if (hasLoadedFromUrlRef.current && urlRouteId === currentRouteId) {
      return; // Already loaded this route
    }

    // Don't load anything if we just cleared the route (currentRouteId is null and urlRouteId is undefined)
    if (!urlRouteId && !currentRouteId && hasLoadedFromUrlRef.current) {
      return; // We're in a cleared state, don't load anything
    }

    const loadRouteFromUrlOrStorage = async () => {
      try {
        // First check URL path parameter (/route/:routeId)
        if (urlRouteId) {
          // Try to load the route - backend RLS will handle access control
          // Public routes will load for everyone, private routes will fail for non-owners
          await handleLoadRoute(urlRouteId);
          
          // Mark that we've loaded from URL to prevent session loading
          hasLoadedFromUrlRef.current = true;
          
          // Keep URL path for route sharing - this enables clean URLs like /route/123
          return; // Exit early if URL parameter was handled
        }

        // Then check sessionStorage for route data from MyRoutesPage
        const storedRouteData = sessionStorage.getItem('loadRoute');
        if (storedRouteData) {
          try {
            const routeData = JSON.parse(storedRouteData);
            if (routeData && routeData.id) {
              // Check if user is authenticated for route access
              if (!user) {
                console.warn('Cannot load route from storage: user not authenticated');
                // Clear loading state and clean up storage
                transitionLoadingState('idle', 'user not authenticated');
                sessionStorage.removeItem('loadRoute');
                return;
              }

              await handleLoadRoute(routeData.id);
              // Mark that we've loaded from storage to prevent session loading
              hasLoadedFromUrlRef.current = true;
              // Clean up sessionStorage after loading
              sessionStorage.removeItem('loadRoute');
            }
          } catch (parseError) {
            console.error('Failed to parse stored route data:', parseError);
            // Clean up invalid data and clear loading state
            sessionStorage.removeItem('loadRoute');
            transitionLoadingState('idle', 'invalid stored route data');
          }
        }
      } catch (error) {
        console.error('Failed to load route from URL parameter or sessionStorage:', error);
        // Mark as loaded and clear loading state to prevent infinite loop even on error
        transitionLoadingState('idle', 'route loading failed');
        hasLoadedFromUrlRef.current = true;
      }
    };

    // Load route from URL parameter or sessionStorage if present
    loadRouteFromUrlOrStorage();
  }, [urlRouteId, user]); // Removed currentRouteId and handleLoadRoute to prevent infinite loops

  // Restore route session from sessionStorage when points are loaded
  useEffect(() => {
    const storedSessionId = sessionStorage.getItem('routeSessionId');
    if (storedSessionId && !routeSessionId) {
      // Restore session for both new routes and editing existing routes
      if (points.length >= 2) {
        setRouteSessionId(storedSessionId);
    } else if (points.length === 0 && !currentRouteId) {
        // Only clean up stale session if we're definitely in a fresh state (no points at all)
        // Don't clean up immediately on page load to allow time for points to be restored
        const cleanupTimer = setTimeout(() => {
          const currentStoredSessionId = sessionStorage.getItem('routeSessionId');
          const currentPoints = points.length;
          if (currentStoredSessionId && currentPoints === 0 && !currentRouteId) {
            sessionStorage.removeItem('routeSessionId');
          }
        }, 1000); // Wait 1 second before cleanup to allow point restoration
        
        return () => clearTimeout(cleanupTimer);
      }
    }
  }, [routeSessionId, currentRouteId, points.length]); // Re-run when these dependencies change

  // Debug session tracking
  useEffect(() => {
    
    // Expose session info on window for debugging
    (window as any).getRouteSession = () => ({
      sessionId: routeSessionId,
      pointsCount: points.length,
      hasSession: !!routeSessionId,
      routeId: currentRouteId,
      hasUnsavedChanges: hasUnsavedChanges
    });
    
    // Also expose the internal function
    (window as any).getRouteSessionInfo = getRouteSessionInfo;
  }, [routeSessionId, points.length, getRouteSessionInfo, currentRouteId, hasUnsavedChanges]);

  // Handlers for calculator settings changes from DetailPanel
  const handlePowerToWeightChange = useCallback((newValue: number) => {
    setPowerToWeight(newValue);
    localStorage.setItem('cyclingbuddy_power_to_weight', newValue.toString());
    
    // Mark route as having unsaved changes when power setting changes
    if (currentRouteId && !hasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  }, [currentRouteId, hasUnsavedChanges]);

  const handleAutoModeChange = useCallback((autoMode: boolean) => {
    setShowOriginal(autoMode);
    localStorage.setItem('cyclingbuddy_duration_auto_mode', autoMode.toString());
    
    // Mark route as having unsaved changes when auto mode changes
    if (currentRouteId && !hasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  }, [currentRouteId, hasUnsavedChanges]);

  return (
    <>
      {/* SEO Meta Tags */}
      <SEO
        title={currentRouteName || 'Cycling Buddy - Cycling Route Planner'}
        description={
          currentRouteName 
            ? `${currentRouteName} - Planned route with Cycling Buddy. Discover this ${routeData ? `${Math.round(routeData.distance / 1000)}km` : ''} cycling route with detailed elevation profile.`
            : 'Plan your perfect cycling route with Cycling Buddy. Interactive route planner with elevation profile for road bike, gravel, MTB and trekking.'
        }
        keywords={`${currentRouteName ? `${currentRouteName}, ` : ''}cycling route planner, bike route planning, ${routeProfile.toLowerCase()}, elevation profile, gpx`}
        url={urlRouteId ? `https://cycling-buddy.app/route/${urlRouteId}` : 'https://cycling-buddy.app/'}
        canonical={urlRouteId ? `https://cycling-buddy.app/route/${urlRouteId}` : 'https://cycling-buddy.app/'}
        type={urlRouteId ? 'article' : 'website'}
        structuredData={{
          "@context": "https://schema.org",
          "@type": urlRouteId ? "Article" : "WebApplication",
          "name": currentRouteName || "Cycling Buddy",
          "description": currentRouteName 
            ? `${currentRouteName} - Planned route with Cycling Buddy`
            : "Smart cycling route planner with elevation profile",
          "url": urlRouteId ? `https://cycling-buddy.app/route/${urlRouteId}` : 'https://cycling-buddy.app/',
          "applicationCategory": "TravelApplication",
          "operatingSystem": "Web Browser",
          ...(currentRouteName && routeData && {
            "headline": currentRouteName,
            "author": {
              "@type": "Organization",
              "name": "Cycling Buddy"
            },
            "publisher": {
              "@type": "Organization", 
              "name": "Cycling Buddy",
              "logo": {
                "@type": "ImageObject",
                "url": "https://cycling-buddy.app/android-chrome-512x512.png"
              }
            },
            "dateCreated": new Date().toISOString(),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://cycling-buddy.app/route/${urlRouteId}`
            }
          })
        }}
      />
      
      <div 
        className={`cycling-map-page${activeStatType ? ' detail-open' : ''}${routeData ? ' has-stats' : ''}${isDetailPanelExpanded && !isLandscape ? ' detail-expanded' : ''}`}
        style={{ 
          position: 'relative', // Enable absolute positioning for overlays
          overflow: 'hidden',
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
      {/* Route Profile Selector Bar - Hidden in landscape mode on mobile */}
      {!isLandscape && (
        <RouteProfileSelector
          selectedProfile={routeProfile}
          onProfileChange={handleRouteProfileChange}
          variant="bar"
        />
      )}

      {/* Map as background layer - takes remaining space */}
      <div 
        className="map-section" 
        style={{ 
          flex: 1, // Take remaining space
          position: 'relative'
        }}
      >
        <CyclingMap
          points={points}
          route={routeData?.coordinates || []}
          isLoading={isLoadingEnhanced}
          hasRoutingError={hasRoutingError}
          isSavingRoute={isSavingRoute}
          onMapClick={handleMapClick}
          onMarkerClick={handleMarkerClick}
          onMarkerDrag={handleMarkerDrag}
          activePoint={activeHoverPoint}
          onHover={handleHover}
          detailPanelOpen={!!activeStatType}
          onFitMapToRoute={handleFitMapToRoute}
          onAddRoutePointViaSearch={handleAddRoutePointViaSearch}
          onManualFitToRoute={fitMapToAllPoints}
          onDeleteRoute={handleDeleteRoute}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onReverseRoute={handleReverseRoute}
          onDownload={handleDownload}
          onGpxDownload={handleGpxDownload}
          onResetRoute={handleResetRoute}
          onOverwriteSave={handleOverwriteSave}
          canUndo={routeHistory.length > 0}
          canRedo={redoHistory.length > 0}
          hasRoutePoints={points.length > 0}
          routePointsCount={points.length}
          hasLoadedRoute={!!currentRouteId}
          hasUnsavedChanges={hasUnsavedChanges}
          currentRouteOwnerId={currentRouteOwnerId}
          currentUserId={user?.id || null}
          onRouteRendered={handleRouteRendered}
          highlightedSurface={highlightedSurface}
          highlightedSegmentIndex={highlightedSegmentIndex}
          routeSegments={routeData?.segments || []}
          highlightedClimbIndex={highlightedClimbIndex}
          elevationData={elevationData}
          onMapStateChange={handleMapStateChange}
          initialCenter={mapCenter}
          initialZoom={mapZoom}
          targetCenter={targetMapCenter}
          targetZoom={targetMapZoom}
          shouldUpdateMapView={shouldUpdateMapView}
          routeData={routeData || undefined}
          routeProfile={routeProfile}
        />
        
        {/* Route Name Overlay */}
        <RouteNameOverlay 
          routeName={currentRouteName} 
          hasUnsavedChanges={hasUnsavedChanges}
          hasRouteInProgress={points.length > 0}
          isPublic={currentRouteIsPublic}
        />
        
        {/* Enhanced routing progress indicator - handles both progress and error states */}
        <RoutingProgressIndicator
          isVisible={isEnhancedCalculating || enhancedError !== null}
          isCalculating={isEnhancedCalculating}
          progress={enhancedProgress}
          progressPercent={enhancedProgressPercent}
          timeElapsed={enhancedTimeElapsed}
          targetDuration={enhancedTargetDuration}
          error={enhancedError}
          canCancel={canCancelEnhanced}
          onCancel={handleCancelWithUndo}
          onRetry={retryLastCalculation}
          onDeleteRoute={handleDeleteRoute}
          onUndo={handleUndoFromError}
          canUndo={routeHistory.length > 0}
          isDetailPanelOpen={!!activeStatType}
          isDetailPanelExpanded={isDetailPanelExpanded}
        />
      </div>
      
      {/* StatsBar and DetailPanel as unified overlay */}
      {routeData && (
        <>
          {/* Hidden calculator that runs in background to keep all calculator state updated */}
          <RealisticPowerCalculator 
            key={`hidden-calculator-${routeData.distance}-${routeData.duration}`}
            routeData={routeData}
            headless={true}
            controlledPowerToWeight={powerToWeight}
            controlledShowOriginal={showOriginal}
            onStateChange={(state) => {
              setCalculatedDuration(state.duration);
              setPowerToWeight(state.powerToWeight);
              setShowOriginal(state.showOriginal);
              setAverageSpeed(state.averageSpeed);
              setSpeedChartData(state.speedChartData);
            }}
          />
          
          <div 
            className="stats-detail-overlay"
            style={{
              transform: (() => {
                const shouldApplyDrag = isDragging && !isTransitioning;
                const transformValue = shouldApplyDrag ? `translateY(${dragOffset}px)` : 'translateY(0px)';

                return transformValue;
              })(),
              transition: (isDragging && !isTransitioning) ? 'none' : 'transform 0.15s ease-out',
            }}
          >
          {/* SwipeHandle - rendered first (appears at top when DetailPanel is open) - hidden in landscape */}
          {activeStatType && !isLandscape && (
            <SwipeHandle 
              activeStatType={activeStatType}
              onStatClick={handleStatClick}
              isExpanded={isDetailPanelExpanded}
              onExpandToggle={() => setIsDetailPanelExpanded(!isDetailPanelExpanded)}
              onDragChange={undefined} // Disable SwipeHandle drag - let StatsBar handle it
            />
          )}
          {/* StatsBar - rendered second (appears below SwipeHandle when DetailPanel is open) */}
          <StatsBar
            distance={routeData.distance}
            duration={calculatedDuration ?? routeData.duration}
            ascend={routeData.ascend}
            routeProfile={routeProfile}
            isLoading={isLoadingEnhanced}
            activeStatType={activeStatType}
            onStatClick={handleStatClick}
            routeSegments={routeData?.segments || []}
            isExpanded={isDetailPanelExpanded}
            onDragChange={activeStatType ? handleDragChange : undefined}
          />
          {/* DetailPanel - rendered third/last (appears at bottom when open) */}
          {activeStatType && (
            <DetailPanel
              activeStatType={activeStatType}
              points={points}
              elevationData={elevationData}
              routeData={routeData}
              onPointDelete={handlePointDelete}
              onPointEdit={handlePointEdit}
              onPointInsert={handlePointInsert}
              onPointsReorder={handlePointsReorder}
              onManualGeocode={handleManualGeocode}
              geocodingLoadingPoints={geocodingLoadingPoints}
              onHover={handleHover}
              onClearStickyHover={clearStickyHover}
              activeHoverPoint={activeHoverPoint}
              scrollToPoint={scrollToPoint}
              deletingPointId={deletingPointId}
              onSurfaceHighlight={handleSurfaceHighlight}
              onClimbHighlight={handleClimbHighlight}
              onClearSurfaceHighlight={handleClearSurfaceHighlight}
              highlightedSurface={highlightedSurface}
              highlightedClimbIndex={highlightedClimbIndex}
              isExpanded={isDetailPanelExpanded}
              isDragging={isDragging}
              onExpandToggle={() => {
                const newExpandedState = !isDetailPanelExpanded;
                setIsDetailPanelExpanded(newExpandedState);
              }}
              routeSourceType={currentRouteSourceType}
              // Pass calculator state from single source of truth
              calculatedDuration={calculatedDuration}
              powerToWeight={powerToWeight}
              showOriginal={showOriginal}
              averageSpeed={averageSpeed}
              speedChartData={speedChartData}
              onPowerToWeightChange={handlePowerToWeightChange}
              onAutoModeChange={handleAutoModeChange}
              // onDurationCalculated removed - handled by hidden calculator above
            />
          )}
          </div>
        </>
      )}
      
      {/* Save Route Modal */}
      <SaveRouteModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onDownload={handleGpxDownload}
        onSave={handleRouteSave}
        currentBikeProfile={routeProfile}
        onValidateRouteName={handleValidateRouteName}
        routeMetrics={routeData ? {
          distance: routeData.distance,
          duration: routeData.duration,
          elevationGain: routeData.ascend
        } : undefined}
        disabled={hasRoutingError}
      />
      
      {/* Download Modal */}
      <DownloadModal
        open={downloadModalOpen}
        title="Generating GPX File"
        message="Calculating route and generating GPX file..."
        allowClose={false}
      />
      
      {/* Welcome Modal for first-time visitors */}
      <WelcomeModal
        open={welcomeModalOpen}
        onClose={handleCloseWelcomeModal}
      />
      </div>
    </>
  );
};

// Memoized component to prevent unnecessary re-renders
const CyclingMapPage = React.memo(CyclingMapPageComponent);

export default CyclingMapPage;